import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../services/api';
import { ApiKey, ApiResponse } from '../../types';

interface ApiKeysState {
  apiKeys: ApiKey[];
  loading: boolean;
  error: string | null;
}

const initialState: ApiKeysState = {
  apiKeys: [],
  loading: false,
  error: null,
};

export const fetchApiKeys = createAsyncThunk(
  'apiKeys/fetchApiKeys',
  async () => {
    const response = await api.get<ApiResponse<ApiKey[]>>('/admin/api-keys');
    return response.data.data;
  }
);

export const createApiKey = createAsyncThunk(
  'apiKeys/createApiKey',
  async (keyData: { name: string; description?: string; environment?: string }) => {
    const response = await api.post<ApiResponse<ApiKey>>('/admin/api-keys', keyData);
    return response.data.data;
  }
);

export const deleteApiKey = createAsyncThunk(
  'apiKeys/deleteApiKey',
  async (keyId: string) => {
    await api.delete(`/admin/api-keys/${keyId}`);
    return keyId;
  }
);

const apiKeysSlice = createSlice({
  name: 'apiKeys',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch API keys
      .addCase(fetchApiKeys.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchApiKeys.fulfilled, (state, action: PayloadAction<ApiKey[]>) => {
        state.loading = false;
        state.apiKeys = action.payload;
      })
      .addCase(fetchApiKeys.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch API keys';
      })
      // Create API key
      .addCase(createApiKey.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createApiKey.fulfilled, (state, action: PayloadAction<ApiKey>) => {
        state.loading = false;
        state.apiKeys.unshift(action.payload);
      })
      .addCase(createApiKey.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create API key';
      })
      // Delete API key
      .addCase(deleteApiKey.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteApiKey.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.apiKeys = state.apiKeys.filter(key => key.id !== action.payload);
      })
      .addCase(deleteApiKey.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete API key';
      });
  },
});

export const { clearError } = apiKeysSlice.actions;
export default apiKeysSlice.reducer;